import { selectUserContext } from 'core/userContext';
import { useUsageLimitsContext } from 'modules/billing/contexts/UsageLimitsContext';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { Export16 } from 'ui/icons';
import { downloadAsCsvFileWithHeaders } from 'utils/csv';
import { useBillingQueryContext } from '../../../contexts';
import { Project } from '../../../types';
import {
  createGlobalSmsCsvRows,
  createProjectBreakdownFileName,
  GLOBAL_SMS_CSV_HEADERS,
  mapGlobalSmsMetricsToRowKey,
} from '../../../utils';

export const GlobalSmsExportProjectUsageButton = () => {
  const [projectMetricsByRowKey, setProjectMetricsByRowKey] = useState({} as any);
  const { dateRange, projectMetrics } = useBillingQueryContext();
  const { orgId, companyName } = useSelector(selectUserContext);
  const { isBillingLimitsEnabled } = useUsageLimitsContext();

  useEffect(() => {
    /**
     * It is only necessary to keep separate state for projectMetricsByRowKey
     * for evaluating `hasProjectMetrics` below, which is used to disable the
     * export button when there are no project metrics to export.
     */
    const byRowKey = mapGlobalSmsMetricsToRowKey(projectMetrics);
    setProjectMetricsByRowKey(byRowKey);
  }, [projectMetrics.length]);

  const hasProjectMetrics = Object.keys(projectMetricsByRowKey)?.length > 0;

  const onClick = () => {
    if (!hasProjectMetrics) return;

    const dateRangeValues = {
      startDate: dateRange.startDate?.toISOString().split('T')[0] as string,
      endDate: dateRange.endDate?.toISOString().split('T')[0] as string,
    };
    const csvRows = createGlobalSmsCsvRows(
      projectMetricsByRowKey,
      { orgId: orgId as number, companyName: companyName as string },
      dateRangeValues,
    );

    const project = projectMetrics?.[0]?.project as Project;
    const fileName = createProjectBreakdownFileName(project);

    downloadAsCsvFileWithHeaders({
      headers: GLOBAL_SMS_CSV_HEADERS,
      data: csvRows,
      fileName,
    });
  };

  return (
    <Button
      data-test="export-global-sms-button"
      onClick={onClick}
      isDisabled={!hasProjectMetrics}
      variant={isBillingLimitsEnabled ? BUTTON_VARIANTS.TEXT : BUTTON_VARIANTS.GHOST}
    >
      <span>Export project breakdown</span>
      <Export16 />
    </Button>
  );
};
